import { useEffect, useState } from "react";
import { Phone, UploadCloud, RefreshCw, Plus, Trash2, ShieldCheck, Info, Loader2, Stethoscope, CheckCircle2, XCircle, Webhook, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function Section({ title, subtitle, children, right }) {
  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-base">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </Card>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function WhatsAppCallingPage() {
  const [cfg, setCfg] = useState(null);
  const [graphVersion, setGraphVersion] = useState("");
  const [meta, setMeta] = useState({ tenant_id: "", webhook_path: "" });
  const [form, setForm] = useState({});
  const [queues, setQueues] = useState([]);
  const [ivrs, setIvrs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState("");
  const [diag, setDiag] = useState(null);
  const [newRoute, setNewRoute] = useState({ name: "", number: "", dest: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function load() {
    try {
      const [c, q, i, r, p] = await Promise.all([
        api.get("/admin/wa-calling/config"),
        api.get("/admin/resources/queues"),
        api.get("/admin/resources/ivr_flows"),
        api.get("/admin/resources/wa_inbound_routes"),
        api.get("/admin/wa-calling/permissions"),
      ]);
      setCfg(c.data.config);
      setGraphVersion(c.data.graph_version);
      setMeta({ tenant_id: c.data.tenant_id, webhook_path: c.data.webhook_path });
      const cc = c.data.config || {};
      setForm({
        enabled: !!cc.enabled,
        phone_number_id: cc.phone_number_id || "",
        waba_id: cc.waba_id || "",
        business_number: cc.business_number || "",
        access_token: "",
        sip_hostname: cc.sip_hostname || "",
        sip_port: cc.sip_port || 5061,
        srtp: cc.srtp || "dtls",
        inbound_dest: cc.inbound_route_id ? `${cc.inbound_route_kind}:${cc.inbound_route_id}` : "",
        cpr_template_name: cc.cpr_template_name || "",
        cpr_template_language: cc.cpr_template_language || "en_US",
        webhook_verify_token: c.data.webhook_verify_token || "connect7-wa",
      });
      setQueues(q.data || []);
      setIvrs(i.data || []);
      setRoutes(r.data || []);
      setPerms(p.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }
  useEffect(() => { load(); }, []);

  const destOptions = [
    ...queues.map((x) => ({ v: `queue:${x.id}`, label: `Queue · ${x.name}` })),
    ...ivrs.map((x) => ({ v: `ivr:${x.id}`, label: `IVR · ${x.name}` })),
  ];

  async function save() {
    setBusy("save");
    try {
      const [kind, id] = (form.inbound_dest || "").split(":");
      const payload = {
        enabled: form.enabled,
        phone_number_id: form.phone_number_id,
        waba_id: form.waba_id,
        business_number: form.business_number,
        sip_hostname: form.sip_hostname,
        sip_port: Number(form.sip_port) || 5061,
        srtp: form.srtp,
        inbound_route_kind: kind || "",
        inbound_route_id: id || "",
        cpr_template_name: form.cpr_template_name,
        cpr_template_language: form.cpr_template_language,
        webhook_verify_token: (form.webhook_verify_token || "").trim() || "connect7-wa",
      };
      if (form.access_token && form.access_token.trim()) payload.access_token = form.access_token.trim();
      const r = await api.put("/admin/wa-calling/config", payload);
      setCfg(r.data.config);
      set("access_token", "");
      toast.success("WhatsApp Calling settings saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  }

  async function pushToMeta() {
    setBusy("push");
    try {
      const r = await api.post("/admin/wa-calling/push-to-meta");
      setCfg(r.data.config);
      toast.success(r.data.sip_password_received
        ? "Pushed to Meta — SIP password received & stored."
        : "Pushed to Meta (no SIP password returned — check settings).");
      load();
    } catch (e) { toast.error(formatErr(e)); runDiagnose(); } finally { setBusy(""); }
  }

  async function runDiagnose() {
    setBusy("diag"); setDiag(null);
    try {
      const r = await api.get("/admin/wa-calling/diagnose");
      setDiag(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  }

  const webhookUrl = meta.webhook_path ? `${process.env.REACT_APP_BACKEND_URL}${meta.webhook_path}` : "";
  function copy(text, label) {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  }

  async function fetchSettings() {
    setBusy("fetch");
    try {
      const r = await api.get("/admin/wa-calling/fetch-settings");
      toast.success("Fetched current settings from Meta");
      console.log("[wa-calling] Meta settings", r.data.settings);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  }

  async function addRoute() {
    if (!newRoute.number.trim()) { toast.error("Enter the WhatsApp number"); return; }
    if (!newRoute.dest) { toast.error("Pick a destination"); return; }
    const [kind, id] = newRoute.dest.split(":");
    setBusy("addroute");
    try {
      await api.post("/admin/resources/wa_inbound_routes", {
        name: newRoute.name || `WA ${newRoute.number}`,
        is_active: true,
        data: { number: newRoute.number, route_to_kind: kind, route_to_id: id },
      });
      setNewRoute({ name: "", number: "", dest: "" });
      toast.success("Inbound route added");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  }

  async function delRoute(id) {
    try { await api.delete(`/admin/resources/wa_inbound_routes/${id}`); load(); toast.success("Route removed"); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="wa-calling-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
          <Phone className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-2xl">WhatsApp Calling</h1>
          <p className="text-sm text-muted-foreground">Voice calls over WhatsApp via Meta Cloud API (SIP mode) — routed through your existing queues &amp; softphone.</p>
        </div>
        <div className="ml-auto">
          <Badge variant="outline" className="gap-1">Graph {graphVersion}</Badge>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-[12px] text-amber-800 dark:text-amber-300" data-testid="wa-calling-liveonly-note">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Real WhatsApp audio requires your <b>live public Asterisk</b> with a valid TLS certificate covering the SIP hostname you register with Meta. Configuration &amp; the Meta push are verified here; run <b>Telephony → Regenerate</b> and restart Asterisk to apply.</span>
      </div>

      <Section
        title="Meta Cloud API (SIP mode)"
        subtitle="Enter your WABA credentials, then push SIP settings to Meta with one click."
        right={
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">Enabled</span>
              <Switch data-testid="wa-enabled-toggle" checked={!!form.enabled} onCheckedChange={(v) => set("enabled", v)} />
            </div>
            <span className="text-[10px] text-muted-foreground">Agents see the change after their next login/refresh.</span>
          </div>
        }
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business WhatsApp number (E.164 digits)" hint="SIP username — digits only, no '+'.">
            <Input data-testid="wa-business-number" value={form.business_number || ""} onChange={(e) => set("business_number", e.target.value)} placeholder="15551234567" />
          </Field>
          <Field label="Phone Number ID">
            <Input data-testid="wa-pnid" value={form.phone_number_id || ""} onChange={(e) => set("phone_number_id", e.target.value)} placeholder="123456789012345" />
          </Field>
          <Field label="WABA ID (optional)">
            <Input data-testid="wa-waba-id" value={form.waba_id || ""} onChange={(e) => set("waba_id", e.target.value)} placeholder="987654321098765" />
          </Field>
          <Field label="System-user access token" hint={cfg?.access_token_set ? "A token is saved. Leave blank to keep it." : "whatsapp_business_management + whatsapp_business_messaging"}>
            <Input data-testid="wa-access-token" type="password" value={form.access_token || ""} onChange={(e) => set("access_token", e.target.value)} placeholder={cfg?.access_token_set ? "•••••• (unchanged)" : "EAAG…"} />
          </Field>
          <Field label="SIP hostname" hint="Must match your Asterisk TLS certificate SAN.">
            <Input data-testid="wa-sip-hostname" value={form.sip_hostname || ""} onChange={(e) => set("sip_hostname", e.target.value)} placeholder="sip.yourcompany.com" />
          </Field>
          <Field label="SIP port">
            <Input data-testid="wa-sip-port" type="number" value={form.sip_port || 5061} onChange={(e) => set("sip_port", e.target.value)} />
          </Field>
          <Field label="SRTP key exchange">
            <Select value={form.srtp || "dtls"} onValueChange={(v) => set("srtp", v)}>
              <SelectTrigger data-testid="wa-srtp"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dtls">DTLS (recommended)</SelectItem>
                <SelectItem value="sdes">SDES</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="SIP password (from Meta)">
            <Input data-testid="wa-sip-pw" disabled value={cfg?.sip_password_set ? "•••••• (received from Meta)" : "Not yet — use Push to Meta"} />
          </Field>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid="wa-save-btn" onClick={save} disabled={busy === "save"} className="gap-2">
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Save
          </Button>
          <Button data-testid="wa-push-btn" onClick={pushToMeta} disabled={busy === "push"} variant="secondary" className="gap-2">
            {busy === "push" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Push to Meta
          </Button>
          <Button data-testid="wa-fetch-btn" onClick={fetchSettings} disabled={busy === "fetch"} variant="outline" className="gap-2">
            {busy === "fetch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Fetch settings
          </Button>
          <Button data-testid="wa-diagnose-btn" onClick={runDiagnose} disabled={busy === "diag"} variant="outline" className="gap-2">
            {busy === "diag" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />} Diagnose
          </Button>
          {cfg?.meta_pushed_at && <span className="text-[11px] text-muted-foreground self-center">Last push: {new Date(cfg.meta_pushed_at).toLocaleString()}</span>}
        </div>

        {diag && (
          <div className="mt-2 rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="wa-diagnose-results">
            <div className="text-[12px] font-semibold flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> Diagnosis</div>
            <ul className="space-y-1.5">
              {(diag.checks || []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px]" data-testid={`wa-diag-check-${i}`}>
                  {c.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />}
                  <span><b>{c.name}</b> — <span className="text-muted-foreground">{c.detail}</span></span>
                </li>
              ))}
            </ul>
            {diag.conclusion && (
              <div className="rounded-md border border-sky-300 bg-sky-50 dark:bg-sky-950/30 p-2.5 text-[12px] text-sky-900 dark:text-sky-200" data-testid="wa-diag-conclusion">
                <b>What to do:</b> {diag.conclusion}
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title="Webhook (for permission replies)" subtitle="Point Meta's webhook here so the customer's Allow/Deny on a call-permission request reaches Connect7.">
        <Field label="Callback URL" hint="Meta App → WhatsApp → Configuration → Webhooks → Callback URL. Subscribe to the 'messages' field.">
          <div className="flex gap-2">
            <Input data-testid="wa-webhook-url" readOnly value={webhookUrl} className="font-mono text-[12px]" />
            <Button type="button" variant="outline" size="icon" className="shrink-0" data-testid="wa-webhook-url-copy" onClick={() => copy(webhookUrl, "Webhook URL")}><Copy className="w-4 h-4" /></Button>
          </div>
        </Field>
        <Field label="Verify token" hint="Paste this exact value into Meta's 'Verify token' field. Editable — click Save after changing.">
          <div className="flex gap-2">
            <Input data-testid="wa-webhook-verify-token" value={form.webhook_verify_token || ""} onChange={(e) => set("webhook_verify_token", e.target.value)} className="font-mono text-[12px]" placeholder="connect7-wa" />
            <Button type="button" variant="outline" size="icon" className="shrink-0" data-testid="wa-webhook-token-copy" onClick={() => copy(form.webhook_verify_token || "connect7-wa", "Verify token")}><Copy className="w-4 h-4" /></Button>
          </div>
        </Field>
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <Webhook className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Tenant: <span className="font-mono">{meta.tenant_id || "—"}</span>. After editing the verify token, click <b>Save</b>, then set the same token in Meta and hit <b>Verify and save</b> there.</span>
        </div>
      </Section>

      <Section title="Inbound routing" subtitle="Customer-initiated WhatsApp calls route just like a normal inbound call.">
        <Field label="Default destination (all inbound WhatsApp calls)" hint="Used when no specific number route matches.">
          <Select value={form.inbound_dest || ""} onValueChange={(v) => set("inbound_dest", v)}>
            <SelectTrigger data-testid="wa-inbound-default"><SelectValue placeholder="Pick a queue or IVR…" /></SelectTrigger>
            <SelectContent>
              {destOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <p className="text-[11px] text-muted-foreground -mt-2">Remember to click <b>Save</b> after changing the default destination.</p>

        <div className="space-y-2">
          <Label className="text-[12px] font-medium">Per-number routes (optional)</Label>
          <div className="rounded-lg border divide-y" data-testid="wa-routes-list">
            {routes.length === 0 && <div className="p-3 text-[12px] text-muted-foreground">No per-number routes yet.</div>}
            {routes.map((r) => {
              const d = r.data || {};
              const dest = destOptions.find((o) => o.v === `${d.route_to_kind}:${d.route_to_id}`);
              return (
                <div key={r.id} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`wa-route-${r.id}`}>
                  <span className="font-mono font-semibold">{d.number}</span>
                  <span className="text-muted-foreground">→ {dest ? dest.label : `${d.route_to_kind}`}</span>
                  <Button size="icon" variant="ghost" className="ml-auto h-7 w-7 text-rose-600" onClick={() => delRoute(r.id)} data-testid={`wa-route-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-[1fr,1fr,1.3fr,auto] gap-2 items-end">
            <Input data-testid="wa-newroute-number" value={newRoute.number} onChange={(e) => setNewRoute((n) => ({ ...n, number: e.target.value }))} placeholder="WhatsApp number" />
            <Input data-testid="wa-newroute-name" value={newRoute.name} onChange={(e) => setNewRoute((n) => ({ ...n, name: e.target.value }))} placeholder="Label (optional)" />
            <Select value={newRoute.dest} onValueChange={(v) => setNewRoute((n) => ({ ...n, dest: v }))}>
              <SelectTrigger data-testid="wa-newroute-dest"><SelectValue placeholder="Destination…" /></SelectTrigger>
              <SelectContent>{destOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="wa-newroute-add" onClick={addRoute} disabled={busy === "addroute"} className="gap-1.5"><Plus className="w-4 h-4" /> Add</Button>
          </div>
        </div>
      </Section>

      <Section title="Outbound calling" subtitle="Agents place WhatsApp calls manually from the softphone / CRM panel.">
        <div className="text-[13px] text-muted-foreground space-y-2">
          <p>Business-initiated (agent) calls require the customer to <b>grant call permission</b> first. When an agent taps <b>WhatsApp Call</b> and permission isn't granted, Connect7 sends a call-permission request and waits for the customer to accept.</p>
          <p className="text-[12px]">Meta limits: 1 permission request / 24h (max 2 / 7 days); once granted, valid ~7 days; ~5 connected calls / 24h; auto-revoked after 4 unanswered. Business-initiated calling is unavailable in some countries (US, Canada, Egypt, Nigeria, Turkey, Vietnam…).</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Permission-request template name (for outside 24h window)" hint="A pre-approved WhatsApp template with a call-permission-request component. Leave blank to only use free-form requests inside the 24h window.">
            <Input data-testid="wa-cpr-template" value={form.cpr_template_name || ""} onChange={(e) => set("cpr_template_name", e.target.value)} placeholder="support_call_permission" />
          </Field>
          <Field label="Template language">
            <Input data-testid="wa-cpr-lang" value={form.cpr_template_language || "en_US"} onChange={(e) => set("cpr_template_language", e.target.value)} placeholder="en_US" />
          </Field>
        </div>
      </Section>

      <Section title="Call permissions" subtitle="Customers who have granted (or been asked for) call permission.">
        <div className="rounded-lg border divide-y" data-testid="wa-perms-list">
          {perms.length === 0 && <div className="p-3 text-[12px] text-muted-foreground">No permission activity yet.</div>}
          {perms.map((p) => (
            <div key={p.user_wa_id} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`wa-perm-${p.user_wa_id}`}>
              <span className="font-mono">{p.user_wa_id}</span>
              <Badge variant={p.response === "accept" ? "default" : "secondary"} className="text-[10px]">
                {p.response || "requested"}{p.is_permanent ? " · permanent" : ""}
              </Badge>
              {p.expiration_timestamp && <span className="text-[11px] text-muted-foreground">expires {new Date(Number(p.expiration_timestamp) * 1000).toLocaleString()}</span>}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
